import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';

import { comoErroDaApi } from '../../shared/api/erros';
import { chavesDeGlosas, decidirGlosa } from './api/glosas';
import { itemDaGlosa } from './api/mapeadores';
import { ROTULOS_DE_TIPO, TIPOS_DE_GLOSA, type AcaoDaDecisao, type Glosa, type TipoDeGlosa } from './api/tipos';

/**
 * A decisão humana sobre a classificação (`TP-054`, T10, `D12`): confirmar ou
 * corrigir o tipo, motivo OBRIGATÓRIO. A decisão é só inclusão — a
 * classificação original continua gravada e a correção vira dado de medição —
 * por isso uma glosa já decidida mostra a última decisão e permite decidir de
 * novo, sem apagar nada.
 */
export function DecisaoDaGlosa({ glosa }: { glosa: Glosa }): React.JSX.Element {
  const cliente = useQueryClient();
  const [aberto, definirAberto] = useState(false);
  const [acao, definirAcao] = useState<AcaoDaDecisao>('confirmar');
  const [tipoCorrigido, definirTipoCorrigido] = useState<TipoDeGlosa | ''>('');
  const [motivo, definirMotivo] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const resumo =
    glosa.ultimaDecisao === 'confirmar'
      ? 'Classificação confirmada'
      : glosa.ultimaDecisao === 'corrigir' && glosa.tipoCorrigido !== null
        ? `Corrigida para ${ROTULOS_DE_TIPO[glosa.tipoCorrigido]}`
        : null;

  if (!aberto) {
    return (
      <Stack spacing={0.25} alignItems="flex-start" aria-label={`Decisão da glosa ${glosa.guia} · ${itemDaGlosa(glosa)}`}>
        {glosa.ultimaDecisao !== null ? <Typography variant="body2">{resumo ?? 'Decisão registrada'}</Typography> : null}
        <Button
          size="small"
          variant={glosa.ultimaDecisao === null ? 'outlined' : 'text'}
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => {
            definirAberto(true);
          }}
        >
          {glosa.ultimaDecisao === null ? 'Confirmar ou corrigir' : 'Decidir de novo'}
        </Button>
      </Stack>
    );
  }

  const podeDecidir = motivo.trim() !== '' && (acao === 'confirmar' || tipoCorrigido !== '') && !enviando;

  return (
    <Stack spacing={1} aria-label={`Decidir a glosa ${glosa.guia} · ${itemDaGlosa(glosa)}`} sx={{ minWidth: 220, maxWidth: 320 }}>
      {resumo !== null ? (
        <Typography variant="caption" color="text.secondary">
          Última decisão: {resumo}
        </Typography>
      ) : null}
      <TextField
        select
        label="Ação"
        size="small"
        value={acao}
        onChange={(evento) => {
          definirAcao(evento.target.value as AcaoDaDecisao);
        }}
      >
        <MenuItem value="confirmar">Confirmar o tipo</MenuItem>
        <MenuItem value="corrigir">Corrigir o tipo</MenuItem>
      </TextField>
      {acao === 'corrigir' ? (
        <TextField
          select
          label="Tipo correto"
          size="small"
          value={tipoCorrigido}
          onChange={(evento) => {
            definirTipoCorrigido(evento.target.value as TipoDeGlosa);
          }}
        >
          {TIPOS_DE_GLOSA.filter((tipo) => tipo !== glosa.tipo).map((tipo) => (
            <MenuItem key={tipo} value={tipo}>
              {ROTULOS_DE_TIPO[tipo]}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      <TextField
        label="Motivo (obrigatório)"
        size="small"
        value={motivo}
        onChange={(evento) => {
          definirMotivo(evento.target.value);
        }}
        multiline
        minRows={2}
      />
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          disabled={!podeDecidir}
          onClick={() => {
            definirEnviando(true);
            definirErro(null);
            decidirGlosa({
              glosaId: glosa.id,
              acao,
              tipoCorrigido: acao === 'corrigir' && tipoCorrigido !== '' ? tipoCorrigido : null,
              motivo: motivo.trim(),
            })
              .then(() => {
                definirMotivo('');
                definirAcao('confirmar');
                definirTipoCorrigido('');
                definirAberto(false);
                void cliente.invalidateQueries({ queryKey: chavesDeGlosas.raiz });
              })
              .catch((causa: unknown) => {
                definirErro(comoErroDaApi(causa).message);
              })
              .finally(() => {
                definirEnviando(false);
              });
          }}
        >
          Registrar decisão
        </Button>
        <Button
          size="small"
          disabled={enviando}
          onClick={() => {
            definirAberto(false);
            definirErro(null);
          }}
        >
          Cancelar
        </Button>
      </Stack>
      {erro !== null ? (
        <Typography variant="caption" color="error.main" role="alert">
          {erro}
        </Typography>
      ) : null}
    </Stack>
  );
}
